import React from 'react';
import { Search, UserX, Users, UserCheck, Building } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const EmployeeFilters = ({
  searchTerm,
  setSearchTerm,
  selectedDepartment,
  setSelectedDepartment,
  departments,
  showAllEmployees,
  setShowAllEmployees,
  statusFilter = 'active',
  setStatusFilter,
  activeCount,
  totalCount,
  inactiveCount,
  filteredCount
}) => {
  return (
    <>
      <div className="flex items-center space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, code, position, dept..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="employee-search"
          />
        </div>
        
        {/* Department Filter Dropdown */}
        <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
          <SelectTrigger className="w-[180px]" data-testid="department-filter">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map((dept) => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* 3-way status toggle: Active / Inactive / All */}
        <div className="flex gap-1 bg-gray-100 rounded-md p-1" data-testid="emp-status-filter">
          {[
            { k: 'active', l: `Active (${activeCount})`, icon: UserCheck, color: 'emerald' },
            { k: 'inactive', l: `Inactive (${inactiveCount})`, icon: UserX, color: 'rose' },
            { k: 'all', l: `All (${totalCount})`, icon: Users, color: 'violet' },
          ].map(f => {
            const Icon = f.icon;
            const isActive = (statusFilter || (showAllEmployees ? 'all' : 'active')) === f.k;
            return (
              <button
                key={f.k}
                type="button"
                onClick={() => {
                  if (setStatusFilter) setStatusFilter(f.k);
                  if (setShowAllEmployees) setShowAllEmployees(f.k !== 'active');
                }}
                data-testid={`emp-filter-${f.k}`}
                className={`px-3 py-1.5 text-sm font-medium rounded flex items-center gap-1.5 whitespace-nowrap transition-all ${
                  isActive ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className={`h-4 w-4 ${isActive ? `text-${f.color}-600` : ''}`} />
                {f.l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter info banner */}
      {(selectedDepartment !== 'all' || searchTerm) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center text-blue-800">
            <Building className="h-4 w-4 mr-2" />
            <span className="text-sm">
              Showing <strong>{filteredCount}</strong> employee{filteredCount !== 1 ? 's' : ''}
              {selectedDepartment !== 'all' && <> in <strong>{selectedDepartment}</strong></>}
              {searchTerm && <> matching "{searchTerm}"</>}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setSelectedDepartment('all'); setSearchTerm(''); }}
            className="text-blue-700 hover:text-blue-900"
          >
            Clear filters
          </Button>
        </div>
      )}

      {/* Info banner when showing all */}
      {showAllEmployees && inactiveCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center text-amber-800">
            <UserX className="h-4 w-4 mr-2" />
            <span className="text-sm">
              Showing all employees including <strong>{inactiveCount}</strong> resigned/terminated staff
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowAllEmployees(false)}
            className="text-amber-700 hover:text-amber-900"
          >
            Hide inactive
          </Button>
        </div>
      )}
    </>
  );
};

export default EmployeeFilters;
